import Header from "@/components/Header";
import Footer from "@/components/Footer";
import GlobalCTA from "@/components/GlobalCTA";
import LeadMagnet from "@/components/LeadMagnet";
import { Button } from "@/components/ui/button";
import { ArrowRight, BadgeCheck, Calculator, CreditCard, FileCheck, Landmark, ShieldCheck } from "lucide-react";
import orangeProfile from "@/assets/orange-profile.jpg";
import PageSEO from "@/components/PageSEO";
import { financialServiceSchema, mortgageFaqSchema, breadcrumb } from "@/lib/schemas";

const Mortgage = () => {
  const scenarios = [
    { scenario: "Commingled Income", challenge: "Business and personal accounts are mixed, creating \"noise\" for lenders.", fix: "We restructure your documentation to create clean, auditable income trails." },
    { scenario: "1099 Complexity", challenge: "Multiple income streams and diverse revenue make you look \"unstable.\"", fix: "We position your file to prove stability and drive a higher debt-to-income (DTI) ratio." },
    { scenario: "SBA to Residential", challenge: "Transitioning from commercial-only assets to personal residential wealth.", fix: "We bridge the \"language gap\" between commercial entities and residential requirements." },
    { scenario: "Entity Structure Gaps", challenge: "LLC income that doesn't flow to personal returns correctly.", fix: "We re-align the entity structure so banks see the \"real\" money you earn." },
    { scenario: "Write-Off Damage", challenge: "Aggressive tax strategies that accidentally killed your borrowing power.", fix: "We build a 12-month plan to re-balance deductions and restore your leverage." },
  ];

  return (
    <div className="min-h-screen bg-background">
      <PageSEO
        title="Atlanta Mortgage Advisor | NMLS #2512081 | Marquetta Bryant"
        description="Complex mortgage solutions for entrepreneurs, 1099 earners, and investors. NMLS #2512081. FHA, VA, conventional & DSCR loans. Metro Atlanta."
        canonical="https://marquettabryant.com/mortgage"
        jsonLd={[financialServiceSchema, mortgageFaqSchema, breadcrumb([{name:"Home",path:"/"},{name:"Mortgage",path:"/mortgage"}])]}
      />
      <Header />
      <main id="main-content">
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <span className="text-accent font-semibold uppercase tracking-widest text-sm">
                One Real Mortgage
              </span>
              <h1 className="text-4xl md:text-6xl font-bold leading-tight">
                Smart Lending.{" "}
                <span className="text-primary">
                  Your Terms.
                </span>
              </h1>
              <p className="text-sm text-muted-foreground/70">
                Marquetta Bryant | Mortgage Advisor with One Real Mortgage | NMLS #2512081
              </p>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                As a licensed real estate broker and Mortgage Advisor, I protect your financial interests from the loan application to the closing table. Let's find the right loan for your situation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://onerealmortgage.pos.yoursonar.com/?originator=marquetta.bryant@onerealmortgage.com" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="premium" className="group">
                    Apply Now
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-accent/15 to-primary/10 rounded-3xl blur-xl" />
                <img src={orangeProfile} alt="Marquetta Bryant - Mortgage Advisor" className="relative w-full max-w-md rounded-3xl shadow-luxury" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lending Solutions */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Lending Solutions</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              From first-time buyers to seasoned investors, I have the right loan product for you.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Landmark, title: "Conventional Loans", desc: "Traditional financing with competitive rates" },
              { icon: ShieldCheck, title: "FHA Loans", desc: "Low down payment options for first-time buyers" },
              { icon: BadgeCheck, title: "VA Loans", desc: "Exclusive benefits for veterans and military" },
              { icon: Calculator, title: "Investment Loans", desc: "Financing for your real estate portfolio" },
              { icon: CreditCard, title: "Credit Repair Guidance", desc: "Strategies to improve your score" },
              { icon: FileCheck, title: "Pre-Approval", desc: "Get approved before you shop" },
            ].map((item, index) => (
              <div key={index} className="p-6 bg-background border border-border rounded-xl hover:shadow-luxury transition-shadow">
                <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mb-4">
                  <item.icon className="w-7 h-7 text-accent" />
                </div>
                <h3 className="font-bold text-xl mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder-Specific Scenarios */}
      <section className="py-20 md:py-32 bg-secondary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-accent font-semibold uppercase tracking-widest text-sm">
              Founder-Specific Scenarios
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-4 text-secondary-foreground">
              We Solve the "Complex" Files
            </h2>
            <p className="text-secondary-foreground/60 text-lg max-w-2xl mx-auto">
              We specialize in the files that traditional retail banks reject. If your vision is big but your taxes are complicated, we architect the approval.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto overflow-hidden rounded-xl border border-accent/20">
            {/* Table Header */}
            <div className="grid grid-cols-3 bg-secondary-foreground/10 px-6 py-4 border-b border-accent/20">
              <span className="text-accent font-bold text-sm uppercase tracking-wider">Scenario</span>
              <span className="text-accent font-bold text-sm uppercase tracking-wider">The Challenge</span>
              <span className="text-accent font-bold text-sm uppercase tracking-wider">The Marquetta Fix</span>
            </div>
            {/* Table Rows */}
            {scenarios.map((row, index) => (
              <div key={index} className={`grid grid-cols-3 px-6 py-5 ${index !== scenarios.length - 1 ? "border-b border-accent/10" : ""}`}>
                <span className="font-semibold text-secondary-foreground text-sm">{row.scenario}</span>
                <span className="text-secondary-foreground/60 text-sm">{row.challenge}</span>
                <span className="text-secondary-foreground/80 text-sm">{row.fix}</span>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-10">
            <a href="https://onerealmortgage.pos.yoursonar.com/?originator=marquetta.bryant@onerealmortgage.com" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="group bg-accent text-accent-foreground hover:bg-accent/90">
                Request a Capital Review
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Get Pre-Approved?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            Start your mortgage application today and take the first step toward homeownership.
          </p>
          <a href="https://onerealmortgage.pos.yoursonar.com/?originator=marquetta.bryant@onerealmortgage.com" target="_blank" rel="noopener noreferrer">
            <Button size="lg" variant="premium" className="group">
              Start My Mortgage Application
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </a>
        </div>
      </section>

      <LeadMagnet />
      <GlobalCTA />
      </main>
      <Footer />
    </div>
  );
};

export default Mortgage;
